import { useEffect, useState, useMemo } from "react";
import { Loader2, ShieldCheck, ClipboardCheck, Lock } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

function emptyForm() {
    return {
        name: "",
        email: "",
        national_id: "",
        password: "",
        role: "user",
        tabs: [],
        caps: [],
        allBranches: false,
        branches: [],
        sections: [],
        metrics: [],
        finAllBranches: false,
        finBranches: [],
        qcAllBranches: false,
        qcBranches: [],
        notifyQc: true,   // new users default ON — see backend user create
        assignedBranchId: "",   // required for branch_manager
        wmBranchIds: [],        // Waste & Meals home branches (module-owned)
        scLocationIds: [],      // Monthly stock count locations (branches + warehouses)
    };
}

function fromUser(user) {
    const p = user.permissions || {};
    const sales = p.sales || {};
    const fin = p.financials || {};
    const qc = p.qc || {};
    return {
        name: user.name || "",
        email: user.email || "",
        national_id: user.national_id || "",
        password: "",
        role: user.role || "user",
        tabs: p.tabs || [],
        caps: p.caps || [],
        allBranches: sales.branches === "ALL",
        branches: Array.isArray(sales.branches) ? sales.branches : [],
        sections: sales.sections || [],
        metrics: sales.metrics || [],
        finAllBranches: fin.branches === "ALL",
        finBranches: Array.isArray(fin.branches) ? fin.branches : [],
        qcAllBranches: qc.branches === "ALL",
        qcBranches: Array.isArray(qc.branches) ? qc.branches : [],
        notifyQc: !!user.notify_qc_submissions,
        assignedBranchId: user.assigned_branch_id || "",
        wmBranchIds: Array.isArray(user.wm_branch_ids) ? user.wm_branch_ids : [],
        scLocationIds: Array.isArray(user.sc_location_ids) ? user.sc_location_ids : [],
    };
}

function CheckboxGrid({ items, idOf, labelOf, selected, onChange, onSelectAll, onClear, testId }) {
    const { t } = useI18n();
    return (
        <div data-testid={testId} className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[color:var(--ink-muted)]">
                    {selected.length} {t("of")} {items.length}
                </span>
                <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onSelectAll}>
                        {t("select_all")}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClear}>
                        {t("clear_all")}
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto scrollbar-thin pe-1">
                {items.map((it) => {
                    const id = idOf(it);
                    const checked = selected.includes(id);
                    return (
                        <label
                            key={id}
                            className="flex items-center gap-2 rounded-md surface border border-[color:var(--border-soft)] px-2.5 py-2 cursor-pointer hover:bg-[color:rgba(186,217,235,0.25)] transition-colors duration-150"
                        >
                            <Checkbox
                                checked={checked}
                                onCheckedChange={(v) => onChange(id, !!v)}
                            />
                            <span className="text-xs text-[color:var(--ink)] truncate">{labelOf(it)}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

export default function UserFormDialog({ open, onOpenChange, user, branches, scLocations, registry, onSaved }) {
    const { t, tn, lang } = useI18n();
    const { isSuperAdmin, isUserAdmin, hasTab, hasCap } = useAuth();
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    // Role options: super_admin can pick any role; a delegated user-admin can
    // create/edit any role EXCEPT super_admin (server enforces this too).
    const availableRoles = useMemo(() => {
        const all = [
            "ops_manager",
            "quality_manager", "qc_supervisor", "inspector", "branch_manager",
            "warehouse_manager", "kitchen_manager", "maintenance_manager",
            "call_center_manager", "complaints_manager", "it_admin",
            "cctv_monitor", "cctv_manager",
            "user",
        ];
        if (isSuperAdmin) return ["super_admin", ...all];
        if (isUserAdmin) return all;
        return [];
    }, [isSuperAdmin, isUserAdmin]);

    useEffect(() => {
        if (open) {
            if (user) {
                setForm(fromUser(user));
            } else {
                const initial = emptyForm();
                // If the caller can only choose from a restricted set (e.g. QM),
                // seed the form with the first allowed role instead of "user".
                if (!availableRoles.includes(initial.role)) {
                    initial.role = availableRoles[0] || "inspector";
                }
                setForm(initial);
            }
        }
    }, [open, user, availableRoles]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    // A super admin held every grant by role (the server's rule): moving the
    // form away from super_admin starts the editor from an empty grant, so
    // nothing it held — admin caps included — is resent as an explicit tick.
    const setRole = (v) => setForm((f) => {
        if (f.role !== "super_admin" || v === "super_admin") return { ...f, role: v };
        const empty = emptyForm();
        const grant = ["tabs", "caps", "allBranches", "branches", "sections", "metrics",
            "finAllBranches", "finBranches", "qcAllBranches", "qcBranches"];
        return { ...f, ...Object.fromEntries(grant.map((k) => [k, empty[k]])), role: v };
    });
    const toggleIn = (key, id, on) =>
        setForm((f) => ({
            ...f,
            [key]: on ? [...new Set([...f[key], id])] : f[key].filter((x) => x !== id),
        }));
    const toggleTab = (key, on) =>
        setForm((f) => ({
            ...f,
            tabs: on ? [...new Set([...f.tabs, key])] : f.tabs.filter((x) => x !== key),
        }));
    const toggleCap = (key, on) =>
        setForm((f) => ({
            ...f,
            caps: on ? [...new Set([...f.caps, key])] : f.caps.filter((x) => x !== key),
        }));

    const sections = registry?.sales_sections || [];
    const metrics = registry?.sales_metrics || [];
    const capabilities = registry?.capabilities || [];
    const adminCapKeys = registry?.admin_cap_keys || ["user_admin", "infrastructure"];
    const capGroups = ["quality", "cctv", "branch_ops", "waste_meals", "cancellations", "stock_count", "daily_count", "screens", "ai", "maintenance", "cashier_check", "admin"];
    const capGroupLabel = {
        quality: t("perm_cap_group_quality"), cctv: t("perm_cap_group_cctv"),
        branch_ops: t("perm_cap_group_branch_ops"),
        waste_meals: t("perm_cap_group_waste_meals"),
        cancellations: t("perm_cap_group_cancellations"),
        stock_count: t("perm_cap_group_stock_count"),
        daily_count: t("perm_cap_group_daily_count"),
        screens: t("perm_cap_group_screens"), ai: t("perm_cap_group_ai"),
        maintenance: t("perm_cap_group_maintenance"), cashier_check: t("perm_cap_group_cashier_check"),
        admin: t("perm_cap_group_admin"),
    };
    // A cap-only tab («التحليل الذكي», «الصيانة», «متوسط فاتورة الكاشير») is granted by its capability, never as a tab.
    const capOnlyTabs = registry?.cap_only_tabs || ["ai_analysis", "maintenance", "cashier_check"];
    const activeTabs = (registry?.tabs || []).filter((x) => x.status === "active" && !capOnlyTabs.includes(x.key));
    const comingTabs = (registry?.tabs || []).filter((x) => x.status === "coming_soon");
    // A delegated user-admin can only hand out access it holds itself (the
    // server enforces the same rule) — anything else renders locked.
    const targetTabs = ((user?.permissions || {}).tabs) || [];
    const targetCaps = ((user?.permissions || {}).caps) || [];
    const tabLocked = (key) => !isSuperAdmin && !hasTab(key) && !targetTabs.includes(key);
    const capLocked = (key) => !isSuperAdmin && !hasCap(key) && !targetCaps.includes(key);
    // Finance-only branches belong to Financials only — never grantable for Sales.
    const salesBranches = (branches || []).filter((b) => !b.finance_only);
    const showQcScope = form.tabs.includes("qc_inspection") || form.tabs.includes("qc_waste");
    // «فرع العمل (عمليات الفرع)» — the ONE hub binding (`users.wm_branch_ids`)
    // shared by Waste & Meals and Cancellations. Shown whenever any hub
    // capability is ticked; REQUIRED before `wm_submit` / `wm_branch_manage`
    // / `co_submit` / `co_branch_manage` can save, and for the two `co_*`
    // caps every chosen branch must carry a POS (`CO_BRANCH_NO_POS`).
    // NOT for a branch_manager — that role is hard-locked to its assigned
    // branch (`wm_core` ignores `wm_branch_ids` for it), so offering the
    // picker would promise access the module will never grant.
    // `wm_reports` is NOT a hub cap: the add-on never opens the hub by itself.
    const HUB_CAPS = ["branch_ops_full_control", "waste_meals_full_control", "wm_submit", "wm_branch_manage", "wm_admin",
        "co_submit", "co_submit_all_branches", "co_branch_manage", "co_review", "co_admin"];
    // «الهدر وأكل العمال: التقارير» (`wm_reports`) adds the two report tabs to
    // an account that already reaches Waste & Meals — only wm_submit /
    // wm_branch_manage / wm_admin do (`co_*` open the hub nav but not
    // /waste-meals/*), so without one of them it can't be ticked. A W&M master
    // sees the reports anyway: shown as included. A stale tick stays clearable.
    const WM_REPORTS_BASE = ["wm_submit", "wm_branch_manage", "wm_admin"];
    const wmReportsIncluded = form.caps.includes("waste_meals_full_control") || form.caps.includes("branch_ops_full_control");
    const wmReportsNeedsBase = !wmReportsIncluded && !WM_REPORTS_BASE.some((c) => form.caps.includes(c));
    const isBranchManager = form.role === "branch_manager";
    const anyWmCap = HUB_CAPS.some((c) => form.caps.includes(c));
    const showWmScope = anyWmCap && !isBranchManager;
    const showWmLockNote = anyWmCap && isBranchManager;
    // a super_admin's stored caps are the full registry; it never needs a binding
    const isSuperTarget = form.role === "super_admin";
    const wmNeedsBranch = !isSuperTarget && !isBranchManager
        && (form.caps.includes("wm_submit") || form.caps.includes("wm_branch_manage"));
    const coNeedsBranch = !isSuperTarget && (form.caps.includes("co_submit") || form.caps.includes("co_branch_manage"));
    const wmBranches = (branches || []).filter((b) => !b.finance_only && b.is_active !== false);
    const hasPos = (b) => typeof b.pos_branch_id === "number";
    // «مواقع الجرد» (الجرد الشهري) — the stock count's OWN binding
    // (`users.sc_location_ids`, from `GET /sc/locations`): branches AND the
    // warehouses, which are not rows in `branches`. Deliberately NOT in
    // HUB_CAPS, or the «فرع العمل» picker would appear for sc-only accounts.
    // Required before `sc_count` can save; `sc_finance` sees every location
    // and needs none. Not for a branch_manager — locked to its own branch.
    // «الجرد اليومي» REUSES this binding (SPEC sheet-retirement §3.1/§4): the
    // picker also shows for `dk_count` / `dk_review`, and `dk_count` needs a
    // location exactly like `sc_count` (the server's _NEEDS_BINDING["sc"]).
    const SC_CAPS = ["sc_count", "sc_finance", "dk_count", "dk_review"];
    const anyScCap = SC_CAPS.some((c) => form.caps.includes(c));
    const anyDkCap = form.caps.includes("dk_count") || form.caps.includes("dk_review");
    const showScScope = anyScCap && !isBranchManager;
    const showScLockNote = anyScCap && isBranchManager;
    const scNeedsLocation = !isSuperTarget && !isBranchManager && form.caps.includes("sc_count");
    const dkNeedsLocation = !isSuperTarget && !isBranchManager && form.caps.includes("dk_count");
    // an inactive location cannot be bound (SC_LOCATION_INACTIVE) — listed only
    // while it is still ticked, so it can be cleared
    const scLocs = (scLocations || []).filter((l) => l.is_active !== false || form.scLocationIds.includes(l.id));
    // The server re-checks EVERY sent id on each save. With the picker hidden
    // (super_admin, or no stock-count cap ticked) a stale inactive binding
    // could not be unticked, so it is dropped instead of failing the save.
    const scPickerShown = showScScope && form.role !== "super_admin";
    const scInactiveIds = new Set((scLocations || []).filter((l) => l.is_active === false).map((l) => l.id));
    const scLocationIdsToSend = () => (scPickerShown
        ? form.scLocationIds
        : form.scLocationIds.filter((id) => !scInactiveIds.has(id)));
    const scLocLabel = (l) => [tn(l), l.kind === "warehouse" ? t("perm_sc_warehouse_badge") : "",
        l.is_active === false ? t("inactive") : ""].filter(Boolean).join(" — ");

    const onSave = async () => {
        if (!form.name.trim()) return toast.error(t("name") + " — " + t("error_generic"));
        if (!form.email.trim() && !form.national_id.trim()) return toast.error(t("identifier_hint"));
        if (!user && form.password.length < 6) return toast.error(t("password_hint"));
        if (form.role === "branch_manager" && !form.assignedBranchId) {
            return toast.error(t("branch_manager_needs_branch"));
        }
        if ((wmNeedsBranch || (coNeedsBranch && !isBranchManager)) && !form.wmBranchIds.length) {
            return toast.error(t("perm_hub_needs_branch"));
        }
        if (scNeedsLocation && !form.scLocationIds.length) {
            return toast.error(t("perm_sc_needs_location"));
        }
        if (dkNeedsLocation && !form.scLocationIds.length) {
            return toast.error(t("perm_dk_needs_location"));
        }
        if (form.role !== "super_admin" && wmReportsNeedsBase && form.caps.includes("wm_reports")) {
            return toast.error(t("perm_wm_reports_needs_base"));
        }
        if (coNeedsBranch) {
            const chosen = isBranchManager ? [form.assignedBranchId] : form.wmBranchIds;
            const noPos = (branches || []).filter((b) => chosen.includes(b.id) && !hasPos(b));
            if (noPos.length) return toast.error(t("perm_co_branch_no_pos") + ": " + noPos.map(tn).join("، "));
        }
        setSaving(true);
        try {
            // An emptied identifier is sent as "" (never null): the server treats
            // null as "unchanged" and UNSETS the field on "" — message 26 Q1.
            const base = {
                name: form.name.trim(),
                email: form.email.trim(),
                national_id: form.national_id.trim(),
                role: form.role,
                notify_qc_submissions: !!form.notifyQc,
            };
            // A branch_manager is locked to `assigned_branch_id`, so the field
            // is not sent at all for that role — the server refuses a non-empty
            // one with a hard 400, and any value already stored is left alone.
            // Same rule for the stock count's `sc_location_ids` (SC_BM_LOCATION_LOCKED).
            if (!isBranchManager) {
                base.wm_branch_ids = form.wmBranchIds;
                base.sc_location_ids = scLocationIdsToSend();
            }
            // super_admin -> permissions=null (backend grants the full set).
            // Everyone else -> the explicit capability grant below.
            let permissions = null;
            if (form.role !== "super_admin") {
                permissions = {
                    tabs: form.tabs,
                    caps: form.caps,
                    sales: {
                        branches: form.allBranches ? "ALL" : form.branches,
                        sections: form.sections,
                        metrics: form.metrics,
                    },
                    financials: {
                        branches: form.finAllBranches ? "ALL" : form.finBranches,
                    },
                    qc: {
                        branches: form.qcAllBranches ? "ALL" : form.qcBranches,
                    },
                };
            }
            if (form.role === "branch_manager") {
                base.assigned_branch_id = form.assignedBranchId;
            } else if (user && user.role === "branch_manager" && form.role !== "branch_manager") {
                // Role changed AWAY from branch_manager — clear the assignment.
                base.assigned_branch_id = null;
            }
            if (user) {
                await api.patch(`/users/${user.id}`, { ...base, permissions });
            } else {
                await api.post(`/users`, { ...base, password: form.password, permissions });
            }
            toast.success(t("save_success"));
            onSaved && (await onSaved());
            onOpenChange(false);
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = d && typeof d === "object"
                ? (lang === "ar" ? d.message_ar : d.message_en) || d.code
                : d;
            toast.error(msg || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin">
                <DialogHeader>
                    <DialogTitle>{user ? t("edit_user") : t("new_user")}</DialogTitle>
                    <DialogDescription>{t("users_subtitle")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Account */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label>{t("name")}</Label>
                            <Input
                                data-testid={OPS.users.form.name}
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("email")}</Label>
                            <Input
                                data-testid={OPS.users.form.email}
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("national_id")}</Label>
                            <Input
                                data-testid={OPS.users.form.nid}
                                value={form.national_id}
                                onChange={(e) => set("national_id", e.target.value)}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                        {!user ? (
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label>{t("password")}</Label>
                                <Input
                                    type="password"
                                    data-testid={OPS.users.form.password}
                                    value={form.password}
                                    onChange={(e) => set("password", e.target.value)}
                                    placeholder={t("password_hint")}
                                />
                            </div>
                        ) : null}
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label>{t("role")}</Label>
                            <Select value={form.role} onValueChange={setRole}>
                                <SelectTrigger data-testid={OPS.users.form.role}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRoles.map((r) => (
                                        <SelectItem
                                            key={r}
                                            value={r}
                                            data-testid={`user-form-role-option-${r}`}
                                        >
                                            {t(`role_${r}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("identifier_hint")}</p>
                        </div>

                        {/* branch_manager REQUIRES a single branch assignment */}
                        {form.role === "branch_manager" ? (
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label className="flex items-center gap-1.5">
                                    <ClipboardCheck className="h-3.5 w-3.5 text-[color:var(--brand-orange)]" />
                                    {t("branch_manager_branch_label")}
                                    <span className="text-[color:var(--danger)]">*</span>
                                </Label>
                                <Select
                                    value={form.assignedBranchId}
                                    onValueChange={(v) => set("assignedBranchId", v)}
                                >
                                    <SelectTrigger data-testid={OPS.users.form.assignedBranch}>
                                        <SelectValue placeholder={t("branch_manager_branch_placeholder")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {salesBranches.map((b) => (
                                            <SelectItem key={b.id} value={b.id}>
                                                {tn(b)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-[color:var(--ink-muted)]">{t("branch_manager_branch_hint")}</p>
                            </div>
                        ) : null}
                    </div>

                    {/* Permissions — super_admin is the only full-authority
                        account (shows a note); EVERY other role gets the
                        granular capability editor. */}
                    {form.role === "super_admin" ? (
                        <div className="flex items-start gap-2 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 text-sm text-[color:var(--ink-muted)]">
                            <ShieldCheck className="h-4 w-4 text-[color:var(--brand-orange)] mt-0.5 shrink-0" />
                            <span>{t("perm_super_note")}</span>
                        </div>
                    ) : (
                        <div data-testid={OPS.users.permEditor} className="space-y-4">
                            {/* Tabs */}
                            <div data-testid={OPS.users.permTabs}>
                                <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_tabs")}</Label>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    {activeTabs.map((tab) => {
                                        const locked = tabLocked(tab.key);
                                        return (
                                        <label
                                            key={tab.key}
                                            data-testid={OPS.users.permTab(tab.key)}
                                            className={`flex items-center gap-2 rounded-md surface border border-[color:var(--border-soft)] px-3 py-2 ${locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                            title={locked ? t("perm_grant_own_only") : ""}
                                        >
                                            <Checkbox
                                                checked={form.tabs.includes(tab.key)}
                                                disabled={locked}
                                                onCheckedChange={(v) => toggleTab(tab.key, !!v)}
                                            />
                                            <span className="text-xs text-[color:var(--ink)]">{tn(tab)}</span>
                                        </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Capabilities — grouped workflow / screen / admin toggles */}
                            <div data-testid={OPS.users.permCaps} className="space-y-3">
                                <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_capabilities")}</Label>
                                {capGroups.map((g) => {
                                    const items = capabilities.filter((c) => c.group === g);
                                    if (!items.length) return null;
                                    return (
                                        <div key={g} data-testid={OPS.users.capGroup(g)}
                                             className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--ink-faint)] mb-2">{capGroupLabel[g]}</p>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {items.map((cap) => {
                                                    const isAdminCap = adminCapKeys.includes(cap.key);
                                                    const locked = (isAdminCap && !isSuperAdmin) || capLocked(cap.key);
                                                    const held = form.caps.includes(cap.key);
                                                    const isWmReports = cap.key === "wm_reports";
                                                    const included = isWmReports && wmReportsIncluded;
                                                    const needsBase = isWmReports && wmReportsNeedsBase;
                                                    const dim = locked || (needsBase && !held);
                                                    const hint = included ? t("perm_wm_reports_included")
                                                        : needsBase ? t("perm_wm_reports_needs_base") : "";
                                                    return (
                                                        <label
                                                            key={cap.key}
                                                            data-testid={OPS.users.permCap(cap.key)}
                                                            className={`flex items-center gap-2 rounded-md surface border border-[color:var(--border-soft)] px-3 py-2 ${dim ? "opacity-50 cursor-not-allowed" : included ? "cursor-default" : "cursor-pointer"}`}
                                                            title={locked ? (isAdminCap ? t("perm_admin_cap_super_only") : t("perm_grant_own_only")) : hint}
                                                        >
                                                            <Checkbox
                                                                checked={included || held}
                                                                disabled={dim || included}
                                                                onCheckedChange={(v) => toggleCap(cap.key, !!v)}
                                                            />
                                                            {hint ? (
                                                                <span className="min-w-0">
                                                                    <span className="block text-xs text-[color:var(--ink)]">{tn(cap)}</span>
                                                                    <span data-testid="user-form-wm-reports-hint" data-state={included ? "included" : "needs_base"}
                                                                          className="block text-[10px] leading-snug text-[color:var(--ink-faint)]">{hint}</span>
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-[color:var(--ink)]">{tn(cap)}</span>
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* «فرع العمل (عمليات الفرع)» — the ONE hub binding
                                (`users.wm_branch_ids`), never `assigned_branch_id`.
                                Shown once, below the three hub capability blocks. */}
                            {showWmScope ? (
                                <div data-testid={OPS.users.hubBranches}
                                     className="space-y-2 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                    <div>
                                        <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_hub_branches")}</Label>
                                        <p data-testid={OPS.users.hubBranchesHint}
                                           className="mt-0.5 text-[11px] text-[color:var(--ink-faint)]">{t("perm_hub_branches_hint")}</p>
                                        {coNeedsBranch ? (
                                            <p className="mt-0.5 text-[11px] text-[color:var(--warning-soft-fg)]">{t("perm_co_branch_no_pos_hint")}</p>
                                        ) : null}
                                    </div>
                                    <CheckboxGrid
                                        testId="user-form-wm-branches"
                                        items={wmBranches}
                                        idOf={(b) => b.id}
                                        labelOf={(b) => (coNeedsBranch && !hasPos(b)) ? `${tn(b)} — ${t("perm_branch_no_pos_badge")}` : tn(b)}
                                        selected={form.wmBranchIds}
                                        onChange={(id, on) => toggleIn("wmBranchIds", id, on)}
                                        onSelectAll={() => set("wmBranchIds", wmBranches.filter((b) => !coNeedsBranch || hasPos(b)).map((b) => b.id))}
                                        onClear={() => set("wmBranchIds", [])}
                                    />
                                </div>
                            ) : null}

                            {/* Locked role: say WHY the picker is absent, or an
                                admin reads the missing field as a bug. */}
                            {showWmLockNote ? (
                                <div data-testid="user-form-wm-branches-locked"
                                     className="flex items-start gap-2 rounded-lg border border-[color:var(--warning-soft-fg)]/25 bg-[color:var(--warning-soft)] p-3">
                                    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                                    <div>
                                        <Label className="text-xs font-bold text-[color:var(--warning-soft-fg)]">{t("perm_hub_branches")}</Label>
                                        <p className="mt-0.5 text-[11px] leading-relaxed text-[color:var(--warning-soft-fg)]">
                                            {t("perm_wm_bm_locked")}
                                        </p>
                                    </div>
                                </div>
                            ) : null}

                            {/* «مواقع الجرد» — the stock count's own binding,
                                below the «الجرد الشهري» capability block. */}
                            {showScScope ? (
                                <div className="space-y-2 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                    <div>
                                        <Label className="text-xs text-[color:var(--ink-muted)]">
                                            {t("perm_sc_locations")}
                                            {scNeedsLocation || dkNeedsLocation ? <span className="text-[color:var(--danger)]"> *</span> : null}
                                        </Label>
                                        <p data-testid={OPS.users.scLocationsHint}
                                           className="mt-0.5 text-[11px] text-[color:var(--ink-faint)]">{t("perm_sc_locations_hint")}</p>
                                        {anyDkCap ? (
                                            <p data-testid={OPS.dk.userLocationsHint}
                                               className="mt-0.5 text-[11px] text-[color:var(--ink-faint)]">{t("perm_dk_locations_hint")}</p>
                                        ) : null}
                                    </div>
                                    <CheckboxGrid
                                        testId={OPS.users.scLocations}
                                        items={scLocs}
                                        idOf={(l) => l.id}
                                        labelOf={scLocLabel}
                                        selected={form.scLocationIds}
                                        onChange={(id, on) => toggleIn("scLocationIds", id, on)}
                                        onSelectAll={() => set("scLocationIds", scLocs.filter((l) => l.is_active !== false).map((l) => l.id))}
                                        onClear={() => set("scLocationIds", [])}
                                    />
                                </div>
                            ) : null}

                            {showScLockNote ? (
                                <div data-testid={OPS.users.scLockNote} className="flex items-start gap-2 rounded-lg border border-[color:var(--warning-soft-fg)]/25 bg-[color:var(--warning-soft)] p-3">
                                    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                                    <div>
                                        <Label className="text-xs font-bold text-[color:var(--warning-soft-fg)]">{t("perm_sc_locations")}</Label>
                                        <p className="mt-0.5 text-[11px] leading-relaxed text-[color:var(--warning-soft-fg)]">
                                            {t("perm_sc_bm_locked")}
                                        </p>
                                    </div>
                                </div>
                            ) : null}

                            {/* Sales access */}
                            {form.tabs.includes("sales") ? (
                                <div className="space-y-4 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("sales_access")}</h3>

                                    {/* Branches */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_branches")}</Label>
                                            <label className="flex items-center gap-2 text-xs text-[color:var(--ink)] cursor-pointer">
                                                <Switch
                                                    checked={form.allBranches}
                                                    onCheckedChange={(v) => set("allBranches", !!v)}
                                                />
                                                {t("all_branches_grant")}
                                            </label>
                                        </div>
                                        {!form.allBranches ? (
                                            <CheckboxGrid
                                                testId={OPS.users.permBranches}
                                                items={salesBranches}
                                                idOf={(b) => b.id}
                                                labelOf={(b) => tn(b)}
                                                selected={form.branches}
                                                onChange={(id, on) => toggleIn("branches", id, on)}
                                                onSelectAll={() => set("branches", salesBranches.map((b) => b.id))}
                                                onClear={() => set("branches", [])}
                                            />
                                        ) : (
                                            <p className="text-xs text-[color:var(--ink-muted)] px-1">{t("all_branches_grant")}</p>
                                        )}
                                    </div>

                                    {/* Sections */}
                                    <div>
                                        <Label className="text-xs text-[color:var(--ink-muted)] mb-2 block">{t("perm_sections")}</Label>
                                        <CheckboxGrid
                                            testId={OPS.users.permSections}
                                            items={sections}
                                            idOf={(s) => s.key}
                                            labelOf={(s) => tn(s)}
                                            selected={form.sections}
                                            onChange={(id, on) => toggleIn("sections", id, on)}
                                            onSelectAll={() => set("sections", sections.map((s) => s.key))}
                                            onClear={() => set("sections", [])}
                                        />
                                    </div>

                                    {/* Metrics */}
                                    <div>
                                        <Label className="text-xs text-[color:var(--ink-muted)] mb-2 block">{t("perm_metrics")}</Label>
                                        <CheckboxGrid
                                            testId={OPS.users.permMetrics}
                                            items={metrics}
                                            idOf={(m) => m.key}
                                            labelOf={(m) => tn(m)}
                                            selected={form.metrics}
                                            onChange={(id, on) => toggleIn("metrics", id, on)}
                                            onSelectAll={() => set("metrics", metrics.map((m) => m.key))}
                                            onClear={() => set("metrics", [])}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {/* Financials access (separate, more sensitive) */}
                            {form.tabs.includes("financials") ? (
                                <div data-testid={OPS.users.finAccess} className="space-y-3 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                    <div>
                                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("financials_access")}</h3>
                                        <p className="text-xs text-[color:var(--ink-muted)] mt-0.5">{t("financials_access_hint")}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_branches")}</Label>
                                            <label className="flex items-center gap-2 text-xs text-[color:var(--ink)] cursor-pointer">
                                                <Switch
                                                    checked={form.finAllBranches}
                                                    onCheckedChange={(v) => set("finAllBranches", !!v)}
                                                />
                                                {t("all_branches_grant")}
                                            </label>
                                        </div>
                                        {!form.finAllBranches ? (
                                            <CheckboxGrid
                                                testId={OPS.users.permFinBranches}
                                                items={branches}
                                                idOf={(b) => b.id}
                                                labelOf={(b) => tn(b)}
                                                selected={form.finBranches}
                                                onChange={(id, on) => toggleIn("finBranches", id, on)}
                                                onSelectAll={() => set("finBranches", branches.map((b) => b.id))}
                                                onClear={() => set("finBranches", [])}
                                            />
                                        ) : (
                                            <p className="text-xs text-[color:var(--ink-muted)] px-1">{t("all_branches_grant")}</p>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                            {/* Quality branch scope — which branches this user's
                                QC access covers (ignored when "QC: all branches"
                                capability is granted). */}
                            {showQcScope ? (
                                <div data-testid={OPS.users.permQcScope} className="space-y-3 rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                                    <div>
                                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("qc_access")}</h3>
                                        <p className="text-xs text-[color:var(--ink-muted)] mt-0.5">{t("qc_access_hint")}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("perm_branches")}</Label>
                                            <label className="flex items-center gap-2 text-xs text-[color:var(--ink)] cursor-pointer">
                                                <Switch
                                                    checked={form.qcAllBranches || form.caps.includes("qc_all_branches")}
                                                    disabled={form.caps.includes("qc_all_branches")}
                                                    onCheckedChange={(v) => set("qcAllBranches", !!v)}
                                                />
                                                {t("all_branches_grant")}
                                            </label>
                                        </div>
                                        {!(form.qcAllBranches || form.caps.includes("qc_all_branches")) ? (
                                            <CheckboxGrid
                                                testId={OPS.users.permQcBranches}
                                                items={salesBranches}
                                                idOf={(b) => b.id}
                                                labelOf={(b) => tn(b)}
                                                selected={form.qcBranches}
                                                onChange={(id, on) => toggleIn("qcBranches", id, on)}
                                                onSelectAll={() => set("qcBranches", salesBranches.map((b) => b.id))}
                                                onClear={() => set("qcBranches", [])}
                                            />
                                        ) : (
                                            <p className="text-xs text-[color:var(--ink-muted)] px-1">{t("all_branches_grant")}</p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Notifications — shown for every role */}
                    <div className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <Label className="text-sm font-semibold text-[color:var(--ink)]">
                                    {t("notif_qc_submissions_label")}
                                </Label>
                                <p className="text-xs text-[color:var(--ink-muted)] mt-1 leading-relaxed">
                                    {t("notif_qc_submissions_desc")}
                                </p>
                            </div>
                            <Switch
                                data-testid={OPS.users.form.notifyQc}
                                checked={form.notifyQc}
                                onCheckedChange={(v) => set("notifyQc", !!v)}
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        data-testid={OPS.users.form.cancel}
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.users.form.save}
                        onClick={onSave}
                        disabled={saving}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                        {t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
